import React, { useState, useEffect, useMemo } from 'react';
import { Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useAuthStore } from '../stores/authStore';

const UserAvatar = ({ 
  user: propUser, 
  size = 'default', 
  className = '', 
  style = {},
  autoGenerate = true,
  forceGenerate = false // 强制生成新头像，忽略已有的picture
}) => {
  const { user: currentUser } = useAuthStore();
  const [hasError, setHasError] = useState(false);

  const targetUser = propUser || currentUser;

  // 使用 useMemo 缓存生成的头像数据，避免重复计算
  const avatarData = useMemo(() => {
    const username = targetUser?.username || targetUser?.email;
    if (!username || (!autoGenerate && !forceGenerate)) return null;

    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57',
      '#FF9FF3', '#54A0FF', '#5F27CD', '#00D2D3', '#FF9F43',
      '#FF6348', '#2ED573', '#3742FA', '#F368E0', '#FFA502',
      '#FF3838', '#2F3542', '#40739E', '#487EB0', '#8C7AE6',
      '#00A8FF', '#9C88FF', '#FBC531', '#4CD137', '#487EB0'
    ];

    const getInitials = () => {
      const cleaned = username.trim().toUpperCase();
      
      // 如果是中文名，取前两个字符
      if (/[\u4e00-\u9fa5]/.test(cleaned)) {
        return cleaned.substring(0, 2);
      }
      
      // 如果是英文名，取首字母
      const words = cleaned.split(/[\s\-_\.]+/).filter(word => word.length > 0);
      if (words.length >= 2) {
        return words[0].charAt(0) + words[1].charAt(0);
      } else if (words.length === 1) {
        return words[0].charAt(0);
      }
      
      return cleaned.charAt(0) || 'U';
    };

    const getColorForUser = () => {
      let hash = 0;
      for (let i = 0; i < username.length; i++) {
        const char = username.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      const index = Math.abs(hash) % colors.length;
      return colors[index];
    };

    const generateSvgDataUrl = () => {
      const initials = getInitials();
      const backgroundColor = getColorForUser();
      
      // 根据size计算SVG尺寸
      const sizeMap = {
        'small': 24,
        'default': 32,
        'large': 40,
        'x-large': 64
      };
      
      const svgSize = typeof size === 'number' ? size : (sizeMap[size] || 32);
      const fontSize = Math.floor(svgSize * 0.4);
      
      const svg = `
        <svg width="${svgSize}" height="${svgSize}" xmlns="http://www.w3.org/2000/svg">
          <rect width="100%" height="100%" fill="${backgroundColor}" rx="${svgSize * 0.1}"/>
          <text x="50%" y="50%" font-family="'PingFang SC', 'Microsoft YaHei', Arial, sans-serif" 
                font-size="${fontSize}" font-weight="bold" fill="white" 
                text-anchor="middle" dominant-baseline="central">${initials}</text>
        </svg>
      `;
      
      // 转换为 Base64 数据 URL
      const base64 = btoa(unescape(encodeURIComponent(svg.trim())));
      return `data:image/svg+xml;base64,${base64}`;
    };

    return {
      initials: getInitials(),
      backgroundColor: getColorForUser(),
      svgDataUrl: generateSvgDataUrl()
    };
  }, [targetUser?.username, targetUser?.email, autoGenerate, forceGenerate, size]);

  // 决定显示什么头像
  const getAvatarSrc = () => {
    // 如果强制生成或用户没有头像且允许自动生成，使用生成的SVG
    if (forceGenerate || (!targetUser?.picture && autoGenerate && avatarData)) {
      return avatarData.svgDataUrl;
    }
    
    // 如果用户有头像且没有错误，使用用户头像
    if (targetUser?.picture && !hasError) {
      return targetUser.picture;
    }
    
    // 如果有生成的头像数据，使用生成的SVG
    if (avatarData) {
      return avatarData.svgDataUrl;
    }
    
    return null;
  };

  const avatarSrc = getAvatarSrc();

  // 如果有头像源，显示头像
  if (avatarSrc) {
    return (
      <Avatar
        src={avatarSrc}
        size={size}
        style={style}
        className={className}
        icon={<UserOutlined />}
        onError={() => {
          setHasError(true);
          return false;
        }}
      />
    );
  }

  // 默认头像
  return (
    <Avatar
      icon={<UserOutlined />}
      size={size}
      style={style}
      className={className}
    />
  );
};

export default UserAvatar;